import { portalRoutes } from "@/router/routes/portalRoutes";
import { isAnyFlatMobileTariff, isFlatHomeTariff } from "@/utils/tariffUtils";

type WizardEntrySource = "default" | "purchase-intent";
type WizardLockedSimType = "physical" | "esim";

/**
 * How the user entered the onboarding / add-device wizard.
 * Purchase intents lock the SIM type to whatever was ordered.
 */
export type WizardEntry = {
  source?: WizardEntrySource;
  lockedSimType?: WizardLockedSimType;
  /** productId from the purchase intent — fallback when the SIM is blanko */
  optionId?: number;
};

type ResolveWizardOptionIdInput = {
  /** From useCheckSIM — wins after ICCID for non-blanko SIMs */
  simOptionId?: number;
  basketOptionId?: number;
  selectedOptionId?: number;
  wizardEntryOptionId?: number;
};

/**
 * Resolves the tariff product id driving wizard routing.
 * SIM card product wins over purchase-intent basket product when both exist.
 */
export const resolveWizardOptionId = ({
  simOptionId,
  basketOptionId,
  selectedOptionId,
  wizardEntryOptionId,
}: ResolveWizardOptionIdInput): number | undefined =>
  simOptionId ?? basketOptionId ?? selectedOptionId ?? wizardEntryOptionId;

type WizardRouteDecision = {
  includesNumberPorting: boolean;
  isFlatHome: boolean;
};

/** Whether the tariff includes number porting and is a Flat Home tariff. */
export const getWizardRouteDecision = (
  optionId?: number,
): WizardRouteDecision => ({
  includesNumberPorting: isAnyFlatMobileTariff(optionId),
  isFlatHome: isFlatHomeTariff(optionId),
});

type WizardStep = {
  allowedMaxOnboardingStep: number;
  route: string;
};

/** Once the tariff is set: number porting (step 3) if it includes it, else checkout (step 4). */
export const getStepAfterRate = (includesNumberPorting: boolean): WizardStep =>
  includesNumberPorting
    ? {
        allowedMaxOnboardingStep: 3,
        route: portalRoutes.addDevice.numberPorting,
      }
    : { allowedMaxOnboardingStep: 4, route: portalRoutes.addDevice.checkout };

type PreviousWizardPathInput = {
  currentStep: number;
  basePath: string;
  optionId?: number;
  isPrecharged?: boolean;
  wizardEntry?: WizardEntry;
};

type SkippedWizardStepsInput = {
  optionId?: number;
  isPrecharged?: boolean;
  wizardEntry?: WizardEntry;
};

/**
 * Which onboarding/add-device steps a given tariff choice bypasses. Shared by
 * back-navigation (getPreviousWizardPath) and the stepper's step numbering
 * (getAddDeviceStepNumbering) so the two never disagree on what got skipped.
 */
const getSkippedWizardSteps = ({
  optionId,
  isPrecharged = false,
  wizardEntry,
}: SkippedWizardStepsInput) => {
  const { includesNumberPorting, isFlatHome } =
    getWizardRouteDecision(optionId);
  const isPurchaseIntent = wizardEntry?.source === "purchase-intent";

  return {
    skippedSimSelection:
      isPurchaseIntent && wizardEntry?.lockedSimType === "esim",
    skippedRateSelection: isFlatHome || isPrecharged || isPurchaseIntent,
    skippedNumberPorting: !includesNumberPorting,
  };
};

const path = (basePath: string, stepPath: string) => `${basePath}/${stepPath}`;

/**
 * Resolves the Back target for a wizard step.
 *
 * Skipped steps are walked over so the user returns to the last screen they
 * actually used. When the flow skipped straight past every earlier step
 * (Flat Home, or a purchase intent that starts mid-flow), there's nowhere
 * *in this wizard* to go back to - Back then exits the flow entirely,
 * to wherever "Cancel"/cancelling this same flow already goes, rather
 * than going dark with no way out. Only returns `null` for a step number
 * outside this wizard (i.e. there's no Back button to begin with).
 */
export const getPreviousWizardPath = ({
  currentStep,
  basePath,
  optionId,
  isPrecharged = false,
  wizardEntry,
}: PreviousWizardPathInput): string | null => {
  const isFlatHome = isFlatHomeTariff(optionId);
  const { skippedSimSelection, skippedRateSelection, skippedNumberPorting } =
    getSkippedWizardSteps({ optionId, isPrecharged, wizardEntry });

  const exitFallback =
    basePath === portalRoutes.addDevice.base
      ? portalRoutes.cockpit.base
      : portalRoutes.welcome;

  const simSelection = skippedSimSelection
    ? exitFallback
    : path(basePath, "1/sim-selection");
  const rateSelection = skippedRateSelection
    ? simSelection
    : path(basePath, "2/rate-selection");
  const numberPorting = skippedNumberPorting
    ? rateSelection
    : path(basePath, "3/number-porting");

  switch (currentStep) {
    case 2:
      return simSelection;
    case 3:
      return isFlatHome ? exitFallback : rateSelection;
    case 4:
      return isFlatHome ? exitFallback : numberPorting;
    default:
      return null;
  }
};

/**
 * Substeps of the Add Device flow that this tariff choice actually goes
 * through, in order — skipped steps (Flat Home, precharged, purchase-intent)
 * omitted. Shared by the stepper's step numbering and the step-jump dropdown
 * so the two never disagree on what's part of the flow.
 *
 * With no tariff chosen yet (`optionId` undefined), every skip check
 * evaluates false, so this naturally returns the full, unskipped step list —
 * the right fallback while on Sim/Rate selection, before the tariff is known.
 */
export const getAddDeviceSteps = ({
  optionId,
  isPrecharged = false,
  wizardEntry,
}: SkippedWizardStepsInput): string[] => {
  const { skippedSimSelection, skippedRateSelection, skippedNumberPorting } =
    getSkippedWizardSteps({ optionId, isPrecharged, wizardEntry });

  return [
    ...(skippedSimSelection ? [] : ["sim-selection"]),
    ...(skippedRateSelection ? [] : ["rate-selection"]),
    ...(skippedNumberPorting ? [] : ["number-porting"]),
    "checkout",
  ];
};

type AddDeviceStepNumberingInput = SkippedWizardStepsInput & {
  substep: string;
  /** Used while the tariff choice (and therefore what gets skipped) isn't known yet. */
  fallbackTotalSteps: number;
};

/**
 * Step/total shown in the Add Device stepper, adjusted for whichever steps a
 * precharged, Flat Home, or purchase-intent tariff bypasses.
 *
 * Sim selection and Rate selection render *before* the tariff is chosen, so
 * they keep the static fallback numbering — except purchase intents, where
 * wizardEntry.optionId is known from activation.
 */
export const getAddDeviceStepNumbering = ({
  substep,
  optionId,
  isPrecharged,
  wizardEntry,
  fallbackTotalSteps,
}: AddDeviceStepNumberingInput): { step: number; totalSteps: number } => {
  if (wizardEntry?.source === "purchase-intent" && optionId !== undefined) {
    const steps = getAddDeviceSteps({ optionId, isPrecharged, wizardEntry });
    const step = Math.max(steps.indexOf(substep), 0) + 1;

    return {
      step,
      totalSteps: steps.length,
    };
  }

  if (substep !== "number-porting" && substep !== "checkout") {
    return {
      step: substep === "rate-selection" ? 2 : 1,
      totalSteps: fallbackTotalSteps,
    };
  }

  const steps = getAddDeviceSteps({ optionId, isPrecharged, wizardEntry });

  // A stale substep can be revisited (e.g. browser back/forward) after a
  // later tariff change causes it to be skipped now — `indexOf` would then
  // be -1. Clamp to 1 rather than showing a nonsensical "00 / 0N" while the
  // navigation redirects the user away from this now-invalid step.
  const step = Math.max(steps.indexOf(substep), 0) + 1;

  return {
    step,
    totalSteps: steps.length,
  };
};
